import type { Game, Phase } from '../core/game';
import { trackExpeditionLoss } from './combatApi';
import { addMenuTime, hasMenuTimeApi } from './gameApi';
import { mountCombatHud } from './screens/combatHud';
import { mountAccalmie } from './screens/accalmie';
import { mountExpeditionEnd } from './screens/expeditionEnd';
import { mountGate } from './screens/gate';
import { mountInvasionWon } from './screens/invasionWon';
import { mountRunLost } from './screens/runLost';
import { mountTitle } from './screens/title';
import { mountVictory } from './screens/victory';
import { mountAbyssToasts } from './abyssToasts';
import type { BattleView, ScreenCtx } from './types';
import { applyAutoThrow } from './autoThrow';
import { applyWakeLock } from './wakeLock';

/**
 * Coquille DOM — routeur d'écrans piloté par `game.on('phase')`.
 * La vue de combat (canvas) est montée une seule fois dans un container qui
 * occupe tout l'écran ; le HUD de combat est un overlay DOM par-dessus.
 * Voir docs/MVP_DESIGN.md §7-8.
 */
export function mountApp(root: HTMLElement, game: Game, battle: BattleView): void {
  root.innerHTML = '';
  root.classList.add('dod-root');

  // Option « Garder l'écran allumé » (A60) : vaut pour **tous** les écrans,
  // combat compris, tant qu'elle est cochée — appliquée une fois ici, puis
  // suivie par l'adaptateur (visibilité de l'onglet) et la case de l'Accalmie.
  applyWakeLock();

  const battleContainer = document.createElement('div');
  battleContainer.className = 'battle-container';
  battleContainer.hidden = true;
  root.appendChild(battleContainer);
  battle.mount(battleContainer);

  const screenHost = document.createElement('div');
  screenHost.className = 'screen-host';
  root.appendChild(screenHost);

  let unmountCurrent: (() => void) | null = null;
  const ctx: ScreenCtx = { game, battle, navigate: (phase) => renderPhase(phase) };

  // Mémorise la raison d'une Expédition perdue (Serpent, A28) le temps que
  // l'écran `expeditionEnd` la lise — voir `ui/combatApi.ts`.
  trackExpeditionLoss(game);

  // Avis du mode infini (A94) : portes de l'Abîme franchies, Artisan des dés.
  mountAbyssToasts(game);

  function isCombatPhase(phase: Phase): boolean {
    return phase === 'expedition' || phase === 'invasion';
  }

  /**
   * Chrono de partie hors combat (COMMENTS v0.6.0 §2, DECISIONS v0.7.0).
   *
   * Pendant un combat, c'est la vue de combat qui fait avancer `game.tick(dt)`
   * et donc le temps joué ; sur les écrans de menu (titre, Accalmie, écrans de
   * fin) plus personne ne tique, et le compteur se figerait. Ce routeur — le
   * seul endroit qui sait quel écran est affiché — pousse donc une seconde par
   * seconde dans `game.addMenuTime(dt)`, **et seulement quand l'onglet est
   * visible** : une partie laissée en arrière-plan ne compte pas comme jouée.
   */
  const MENU_TICK_MS = 1000;
  let menuTimer: number | undefined;
  function stopMenuClock(): void {
    if (menuTimer === undefined) return;
    window.clearInterval(menuTimer);
    menuTimer = undefined;
  }
  function startMenuClock(): void {
    if (menuTimer !== undefined || !hasMenuTimeApi(game)) return;
    menuTimer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      addMenuTime(game, MENU_TICK_MS / 1000);
    }, MENU_TICK_MS);
  }

  function renderPhase(phase: Phase): void {
    unmountCurrent?.();
    unmountCurrent = null;
    screenHost.innerHTML = '';
    screenHost.classList.toggle('screen-host--overlay', isCombatPhase(phase));

    const showBattle = isCombatPhase(phase);
    battleContainer.hidden = !showBattle;
    // Décor élargi (v0.39.0) : en combat, le canevas 3D déborde de la colonne
    // jusqu'aux bords de la fenêtre sur PC — `#app` cesse de rogner (CSS).
    document.documentElement.toggleAttribute('data-combat', showBattle);
    if (showBattle) {
      // Option « Lancer automatique » (COMMENTS v0.4.1 §2) : appliquée au
      // début de chaque combat, sans rien supposer de la vue de combat.
      applyAutoThrow(battle);
      battle.start();
      stopMenuClock();
    } else {
      battle.stop();
      startMenuClock();
    }

    // `gate` (écran d'achat, A112) : la phase n'existe dans `Phase` qu'avec le cœur A112, d'où le `string`.
    switch (phase as string) {
      case 'title':
        unmountCurrent = mountTitle(screenHost, ctx);
        break;
      case 'expedition':
      case 'invasion':
        unmountCurrent = mountCombatHud(screenHost, ctx);
        break;
      case 'expeditionEnd':
        unmountCurrent = mountExpeditionEnd(screenHost, ctx);
        break;
      case 'accalmie':
        unmountCurrent = mountAccalmie(screenHost, ctx);
        break;
      case 'invasionWon':
        unmountCurrent = mountInvasionWon(screenHost, ctx);
        break;
      case 'runLost':
        unmountCurrent = mountRunLost(screenHost, ctx);
        break;
      // A112 : écran d'achat (jeu complet), après le boss du niveau 2 ou depuis l'Accalmie.
      case 'gate':
        unmountCurrent = mountGate(screenHost, ctx);
        break;
      // Phase 5 (A78, A82) : victoire sur le Tricorne, crédits, mode infini.
      case 'victory':
        unmountCurrent = mountVictory(screenHost, ctx);
        break;
      // v0.5.0 : plus d'écran de Prestige (COMMENTS v0.4.1 §1.2) — la défaite
      // acquittée (`acknowledgeRunLost()`) repart directement en Accalmie.
      default:
        break;
    }
  }

  // Écran titre : porte d'entrée toujours affichée en premier, même si une
  // sauvegarde a repris la partie directement en Accalmie (DECISIONS UI).
  let titleShown = false;
  function initialRender(): void {
    if (!titleShown && game.state.phase !== 'expedition' && game.state.phase !== 'invasion') {
      titleShown = true;
      renderPhase('title');
      return;
    }
    renderPhase(game.state.phase);
  }

  game.on('phase', ({ phase }) => {
    if (!titleShown) return; // le titre gère lui-même la sortie initiale
    renderPhase(phase);
  });

  initialRender();
}
